import React, { useMemo } from 'react';
import { Options, OptionsOrGroups, SingleValue } from 'react-select';
import { CreatableReactSelectMenu } from '../../Dropdown/StyledReactSelect';
import { BasicSingleValue } from '../../Dropdown/StyledReactSelect/SelectUtils';
import * as MeasureDisplayFormatUI from './MeasureDisplayFormatUI';
import {
  ICreatableReactSelectOption,
  IReactSelectOption,
} from '../../../utils/types';

interface IProps {
  value: string | null;
  onChange: (value: string | null) => void;
}

const MAX_FIELD_VALUE_LENGTH = 20;

const defaultUnitOptions: IReactSelectOption[] = [
  {
    label: 'days',
    value: 'days',
  },
  {
    label: 'hours',
    value: 'hours',
  },
  {
    label: 'minutes',
    value: 'minutes',
  },
  {
    label: 'months',
    value: 'months',
  },
  {
    label: 'ms',
    value: 'ms',
  },
  {
    label: 'seconds',
    value: 'seconds',
  },
  {
    label: 'weeks',
    value: 'weeks',
  },
  {
    label: 'years',
    value: 'years',
  },
];

const MeasureDisplayFormatUnit = ({ value, onChange }: IProps) => {
  const handleChangeUnit = (
    option: SingleValue<ICreatableReactSelectOption>,
  ) => {
    onChange(option?.value ?? null);
  };

  const selectedValue: ICreatableReactSelectOption | null = useMemo(() => {
    if (!value) {
      return null;
    }

    const foundOption = defaultUnitOptions.find(
      (option) => option.value === value,
    );

    if (foundOption) {
      return foundOption;
    } else {
      return {
        label: value,
        value,
      };
    }
  }, [value]);

  const handleValidateNewOption = (
    inputValue: string,
    value: Options<IReactSelectOption>,
    options: OptionsOrGroups<IReactSelectOption, never>,
  ) => {
    if (!inputValue?.length) {
      return false;
    }

    // Check length
    if (inputValue.length > MAX_FIELD_VALUE_LENGTH) {
      return false;
    }

    // Check menu value
    if (
      value.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    // Check options
    if (
      options?.length &&
      options.some(
        (option) => option.label.toLowerCase() === inputValue.toLowerCase(),
      )
    ) {
      return false;
    }

    return true;
  };

  return (
    <MeasureDisplayFormatUI.FormRow>
      <MeasureDisplayFormatUI.FormLabel>Unit</MeasureDisplayFormatUI.FormLabel>
      <CreatableReactSelectMenu
        value={selectedValue}
        onChange={handleChangeUnit}
        isMulti={false}
        options={defaultUnitOptions}
        components={{
          SingleValue: BasicSingleValue,
        }}
        styles={{
          control: (base) => ({
            ...base,
            width: '100%',
            minWidth: 'unset',
          }),
        }}
        // Create new option props:
        isValidNewOption={handleValidateNewOption}
        onCreateOption={onChange}
      />
    </MeasureDisplayFormatUI.FormRow>
  );
};

export default MeasureDisplayFormatUnit;
